import { Component, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

export interface WithdrawTicketReason {
  value: string;
  label: string;
  description: string;
}

export interface WithdrawTicketResult {
  reason: string;
  details?: string;
}

export const WITHDRAW_REASONS: WithdrawTicketReason[] = [
  {
    value: 'resolved_on_my_own',
    label: 'I resolved the issue myself',
    description: 'The problem was fixed without needing support assistance'
  },
  {
    value: 'created_by_mistake',
    label: 'Created by mistake',
    description: 'This ticket was submitted in error'
  },
  {
    value: 'duplicate_ticket',
    label: 'Duplicate ticket',
    description: 'I already have another ticket for this issue'
  },
  {
    value: 'no_longer_needed',
    label: 'No longer needed',
    description: 'The request is no longer relevant'
  },
  {
    value: 'other',
    label: 'Other reason',
    description: 'Specify a different reason below'
  }
];

@Component({
  selector: 'app-withdraw-ticket-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './withdraw-ticket-modal.component.html',
  styleUrls: ['./withdraw-ticket-modal.component.scss']
})
export class WithdrawTicketModalComponent {
  @Output() confirmed = new EventEmitter<WithdrawTicketResult>();
  @Output() cancelled = new EventEmitter<void>();

  isOpen = signal(false);
  isProcessing = signal(false);

  ticketNumber: string = '';
  selectedReason: string = '';
  otherDetails: string = '';

  readonly reasons = WITHDRAW_REASONS;

  /**
   * Open the modal
   */
  open(ticketNumber: string): void {
    this.ticketNumber = ticketNumber;
    this.selectedReason = '';
    this.otherDetails = '';
    this.isOpen.set(true);
  }

  /**
   * Close the modal without confirming
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Confirm the withdrawal
   */
  confirm(): void {
    if (!this.selectedReason) return;

    const result: WithdrawTicketResult = {
      reason: this.selectedReason
    };

    if (this.selectedReason === 'other' && this.otherDetails.trim()) {
      result.details = this.otherDetails.trim();
    }

    this.confirmed.emit(result);
    this.isOpen.set(false);
  }

  /**
   * Set processing state (for async operations)
   */
  setProcessing(processing: boolean): void {
    this.isProcessing.set(processing);
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }

  /**
   * Check if confirm button should be enabled
   */
  get canConfirm(): boolean {
    if (!this.selectedReason) return false;
    if (this.selectedReason === 'other' && !this.otherDetails.trim()) return false;
    return true;
  }
}
